import classNames from "classnames";
import { computed, observable, action, runInAction } from "mobx";
import { observer } from "mobx-react";
import React, { forwardRef, Ref, ElementType, ReactNode, CSSProperties, Children } from "react";
import { Transition } from "react-transition-group";

import * as utils from "../../../utils";
import { AnimationDuration } from "../../../styles/Animations";
import Spinner, { SpinnerSize } from "../Spinner/Spinner";
import forceReflow from "../../../utils/forceReflow";
import { handleMouseUpByBlurring } from "../../../utils/focus";
import { ChevronDown, ArrowRight, Tick } from "../../icons";

export enum ButtonType {
  Primary = "primary",
  Secondary = "secondary",
  Tertiary = "tertiary",
}

export enum ButtonState {
  Idle = "idle",
  Waiting = "waiting",
  Success = "success",
}

export enum ButtonSize {
  XSmall = "x-small",
  Small = "small",
  Medium = "medium",
  Large = "large",
}

export enum ButtonDecorator {
  Dropdown = "dropdown",
  Arrow = "arrow",
}

export enum ButtonTheme {
  Light = "light",
  Dark = "dark",
}

export interface IButtonComponentProps {
  /**
   * The type of the button
   */
  type: ButtonType;
  /**
   * The size of the button
   */
  size: ButtonSize;
  /**
   * The width of the button
   */
  width: "normal" | "half" | "full";
  /**
   * Instead of using a click fn, provide a function to get a link
   */
  href?: () => string;
  /**
   * The function to run on click, only triggered when Button is in Idle state
   */
  onClick?: (event: React.MouseEvent<HTMLElement>) => void | Promise<void>;
  /**
   * The function to run on mouse enter.
   */
  onMouseEnter?: (event: React.MouseEvent<HTMLElement>) => void;
  /**
   * The button's id
   */
  id?: string;
  /**
   * Turn an error into a message that can be displayed in a tooltip to the user
   */
  disabled?: boolean;
  /**
   * Additional classes to be applied to the button
   */
  className?: string;
  /**
   * The state of the button; if this prop is set, the button will
   * become a 'controlled' button. Otherwise, it will handle its own
   * transition states between idle, waiting and success.
   */
  buttonState?: ButtonState;
  /**
   * Change the button element type. as="a" changes the element from
   * a button to a link
   */
  as: ElementType;
  /**
   * Rounded button style
   */
  isRound?: boolean;
  /**
   * Special cases for icons on the right
   */
  decorator?: ButtonDecorator;
  /**
   * If true then link opens in same tab
   */
  openInSameTab?: boolean;
  /**
   * Remove padding and equalize width/height for buttons that
   * only contain an icon
   */
  isIconOnly?: boolean;
  /**
   * Light or dark theme
   */
  theme: ButtonTheme;
  /**
   * Buttons that perform a destructive action e.g. delete
   */
  isDestructive?: boolean;
  /**
   * Optional message to show during the success button state.
   */
  successMessage?: string;
  children?: ReactNode;
  style?: CSSProperties;
  innerRef?: Ref<HTMLElement>;
  onMouseDown?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
  tabIndex?: number;
}

@observer
class ButtonComponent extends React.Component<IButtonComponentProps> {
  public static defaultProps = {
    type: ButtonType.Primary,
    size: ButtonSize.Medium,
    width: "normal",
    as: "button",
    theme: ButtonTheme.Light,
  };

  private id: string;

  @observable.ref
  private _internalButtonState: ButtonState;

  @computed
  private get buttonState(): ButtonState {
    return this.isControlled ? (this.props.buttonState as ButtonState) : this._internalButtonState;
  }

  @computed
  private get isControlled(): boolean {
    return !!this.props.buttonState;
  }

  constructor(props: IButtonComponentProps) {
    super(props);
    this.id = props.id ? props.id : `button-${utils.randomString()}`;

    this._internalButtonState = ButtonState.Idle;
  }

  public render(): ReactNode {
    const {
      buttonState,
      onClick,
      successMessage,
      as,
      className,
      type,
      size,
      width,
      isRound,
      disabled,
      innerRef,
      children,
      href,
      decorator,
      openInSameTab,
      isIconOnly,
      theme,
      isDestructive,
      ...rest
    } = this.props;
    // Automatically use an <a /> if given an href
    const ButtonElement = !!href ? "a" : as;
    const isDisabledAndIdle = disabled && this.buttonState === ButtonState.Idle;
    const isLink = as === "a" || !!href;
    const isNonSelfReferencingLink = isLink && !!href && !/^\/[^/]/.test(href());

    return (
      <ButtonElement
        className={classNames([
          "button",
          className,
          `button--${type}`,
          `button--${size}`,
          `button--${width}-width`,
          `button--id-${this.id}`,
          `button--${theme}`,
          {
            "button--icon-only": isIconOnly,
            "button--destructive": isDestructive,
            "button--is-round": isRound,
            "button--is-disabled": disabled,
            [`button--is-${isLink ? ButtonState.Idle : this.buttonState}`]: !isDisabledAndIdle,
          },
        ])}
        id={this.id}
        onClick={this.handleClick}
        onMouseEnter={this.props.onMouseEnter}
        onMouseUp={handleMouseUpByBlurring}
        ref={innerRef}
        disabled={disabled}
        href={!!href ? href() : undefined}
        target={isNonSelfReferencingLink && !openInSameTab ? "_blank" : undefined}
        rel={isNonSelfReferencingLink && !openInSameTab ? "noopener noreferrer" : undefined}
        {...rest}
      >
        <Transition in={this.showContent} timeout={AnimationDuration.Medium} onEnter={forceReflow}>
          {(status): ReactNode => (
            <div className={`button__content button__content--${status}`}>
              {Children.map(children, this.renderChild)}
              {decorator === ButtonDecorator.Dropdown && <ChevronDown className="button__dropdown-icon" />}
              {decorator === ButtonDecorator.Arrow && <ArrowRight className="button__arrow-icon" />}
            </div>
          )}
        </Transition>
        <Transition
          mountOnEnter
          unmountOnExit
          in={this.buttonState === ButtonState.Waiting}
          timeout={AnimationDuration.Medium}
          onEnter={forceReflow}
        >
          {(status): ReactNode => (
            <Spinner size={SpinnerSize.Small} className={`button__spinner button__spinner--${status}`} />
          )}
        </Transition>
        <Transition
          mountOnEnter
          unmountOnExit
          in={this.buttonState === ButtonState.Success}
          timeout={AnimationDuration.Medium}
          onEnter={forceReflow}
        >
          {(status): ReactNode => (
            <div className={`button__success button__success--${status}`}>
              {!!successMessage && (
                <div className="button__success-text" aria-live="polite">
                  {successMessage}
                </div>
              )}

              <Tick className="button__success-icon" />
            </div>
          )}
        </Transition>
      </ButtonElement>
    );
  }

  private renderChild = (child: ReactNode): ReactNode => {
    if (child === null) return;

    // Wrap string children in .button__text
    if (typeof child === "string") {
      return <span className="button__text">{child}</span>;
    }

    // All other children
    return child;
  };

  @action.bound
  private async handleClick(event: React.MouseEvent<HTMLElement>): Promise<any> {
    if (!this.props.onClick) {
      return;
    }

    // If the button is not controlled, and the state is idle,
    // do not trigger the onClick delegate.
    if (!this.isControlled && this._internalButtonState !== ButtonState.Idle) {
      return;
    }

    const result = this.props.onClick(event);

    /**
     * Only transition states if the button is not externally controlled and the result of
     * the onClick delegate is asynchronous.
     */
    const shouldTransitionState = !this.isControlled && result instanceof Promise;
    if (shouldTransitionState) {
      this._internalButtonState = ButtonState.Waiting;

      try {
        await Promise.all([result, utils.delay(500)]);

        runInAction(() => {
          this._internalButtonState = ButtonState.Success;
          setTimeout(() => {
            if (this._internalButtonState === ButtonState.Success) {
              this._internalButtonState = ButtonState.Idle;
            }
          }, 3500);
        });
      } catch (error) {
        this._internalButtonState = ButtonState.Idle;
      }
    }
  }

  @computed
  private get showContent(): boolean {
    return this.buttonState !== ButtonState.Waiting && this.buttonState !== ButtonState.Success;
  }
}

const Button = forwardRef<HTMLElement, JSX.LibraryManagedAttributes<typeof ButtonComponent, IButtonComponentProps>>(
  function button(
    props: JSX.LibraryManagedAttributes<typeof ButtonComponent, IButtonComponentProps>,
    ref: Ref<HTMLElement>,
  ) {
    return <ButtonComponent innerRef={ref} {...props} />;
  },
);

export default Button;
